/**
 * Blog listing with pagination and scroll position management
 */

import { h, render } from 'preact'
import { useState, useEffect } from 'preact/hooks'
import { useScrollCache } from 'libs/searchspring/hooks/useScrollCache'
import { useFilterScroll } from 'libs/searchspring/hooks/useFilterScroll'
import { useScrollPositionKeeper } from 'libs/searchspring/hooks/useScrollPositionKeeper'
import { scrollHistoryManager } from 'helpers/scroll-history'
import clsx from 'clsx'
import Icon from '@/components/snippets/icon/icon'
import { BlogCard } from './BlogCard'
import { BlogCardFeature } from './BlogCardFeature'

const featuredPage = window.globals?.currentBlog?.featuredPage || null
const allArticles = window.globals?.currentBlog?.articles || []
const data = featuredPage ? allArticles.filter((a) => a.id !== featuredPage.id) : allArticles

const ELLIPSIS = 'ellipsis'
const START_BLOCK_SIZE = 5
const MIDDLE_WINDOW_SIZE = 4
const END_BLOCK_SIZE = 5

function getPageNumbers(activePage, totalPages) {
  if (totalPages <= START_BLOCK_SIZE + 1) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  const pages = [1]
  const startThreshold = START_BLOCK_SIZE
  const endThreshold = totalPages - END_BLOCK_SIZE + 1

  if (activePage < startThreshold) {
    for (let i = 2; i <= Math.min(START_BLOCK_SIZE, totalPages); i++) pages.push(i)
    if (totalPages > START_BLOCK_SIZE) {
      pages.push(ELLIPSIS)
      pages.push(totalPages)
    }
    return pages
  }

  if (activePage >= endThreshold) {
    const left = Math.max(2, totalPages - END_BLOCK_SIZE + 1)
    pages.push(ELLIPSIS)
    for (let i = left; i <= totalPages; i++) pages.push(i)
    return pages
  }

  const left = Math.max(2, activePage - (MIDDLE_WINDOW_SIZE - 1))
  const right = Math.min(totalPages - 1, left + MIDDLE_WINDOW_SIZE - 1)
  pages.push(ELLIPSIS)
  for (let i = left; i <= right; i++) pages.push(i)
  pages.push(ELLIPSIS)
  pages.push(totalPages)
  return pages
}

function usePageSync() {
  const [currentPage, setCurrentPage] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const pageParam = urlParams.get('page')
    return pageParam ? parseInt(pageParam, 10) : 1
  })

  useEffect(() => {
    const url = new URL(window.location.href)
    if (currentPage > 1) {
      url.searchParams.set('page', currentPage.toString())
    } else {
      url.searchParams.delete('page')
    }
    if (url.href !== window.location.href) {
      window.history.pushState({ page: currentPage }, '', url.toString())
    }
  }, [currentPage])

  useEffect(() => {
    const onPopState = (event) => {
      const urlParams = new URLSearchParams(window.location.search)
      const pageParam = urlParams.get('page')
      const newPage = event.state?.page ?? (pageParam ? parseInt(pageParam, 10) : 1)
      setCurrentPage(newPage)
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  return [currentPage, setCurrentPage]
}

function BlogPagination({ activePage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null

  const pageNumbers = getPageNumbers(activePage, totalPages)
  const buttonClass = 'btn btn-primary btn-primary-alt btn-border btn-m lg:btn-l'

  const handlePrev = () => {
    if (activePage <= 1) return
    onPageChange(activePage - 1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleNext = () => {
    if (activePage >= totalPages) return
    onPageChange(activePage + 1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handlePage = (page) => {
    if (page === activePage || page === ELLIPSIS) return
    onPageChange(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="flex w-full items-center justify-center">
      <div className="grid w-fit grid-cols-2 items-center gap-1 pb-3.5 md:grid-cols-pagination">
        <button
          type="button"
          className={`${buttonClass} w-full md:w-fit`}
          onClick={handlePrev}
          disabled={activePage <= 1}
          aria-label="Previous page"
        >
          <div className="h-[11px] w-[16px] children:size-full">
            <Icon id="arrow-left" />
          </div>
          <span>Previous</span>
        </button>
        <div className="flex items-center justify-center gap-x-0.5 md-max:col-[1/-1] md-max:row-[1]">
          {pageNumbers.map((page, i) =>
            page === ELLIPSIS ? (
              <span key={`ellipsis-${i}`} className="paragraph-b font-medium">
                ...
              </span>
            ) : (
              <button
                key={page}
                type="button"
                className={[buttonClass, 'btn-square', page === activePage ? 'active' : ''].join(' ')}
                onClick={() => handlePage(page)}
                aria-label={`Page ${page}`}
                aria-current={page === activePage ? 'page' : undefined}
              >
                {page}
              </button>
            )
          )}
        </div>
        <button
          type="button"
          className={`${buttonClass} w-full md:w-fit`}
          onClick={handleNext}
          disabled={activePage >= totalPages}
          aria-label="Next page"
        >
          <span>Next</span>
          <div className="h-[11px] w-[16px] children:size-full">
            <Icon id="arrow-right" />
          </div>
        </button>
      </div>
    </div>
  )
}

function BlogGrid() {
  history.scrollRestoration = 'manual'
  const [currentPage, setCurrentPage] = usePageSync()
  const listEl = typeof document !== 'undefined' ? document.getElementById('list-blogs') : null
  const resultsPerPage = parseInt(listEl?.getAttribute?.('per-page') || '9', 10)
  const totalPages = Math.max(1, Math.ceil(data.length / resultsPerPage))
  const clampedPage = Math.min(Math.max(1, currentPage), totalPages)
  const start = (clampedPage - 1) * resultsPerPage
  const currentBlogs = data.slice(start, start + resultsPerPage)

  useEffect(() => {
    if (currentPage !== clampedPage) setCurrentPage(clampedPage)
  }, [totalPages])

  const { setEntry } = useScrollPositionKeeper(clampedPage)
  const { setScrollElement } = useFilterScroll()
  useScrollCache([currentBlogs, clampedPage])

  if (!featuredPage && (!data || data.length === 0)) {
    return (
      <div>
        <p className="h6">No blog posts found.</p>
      </div>
    )
  }

  return (
    <div className="flex grow flex-col px-margin pt-1.5 md:pt-2.5">
      <div
        ref={(el) => {
          if (el) {
            setScrollElement(el)
            setEntry(el)
          }
        }}
        className={clsx(
          'grid w-full grid-cols-1 gap-x-gutter gap-y-1.5 pb-3.5 [grid-auto-flow:row_dense] md:grid-cols-3 md:gap-y-4 xxl:grid-cols-4',
          currentBlogs.length === 0 && 'min-h-screen-height'
        )}
      >
        {currentBlogs.slice(0, 3).map((blog, index) => (
          <BlogCard blog={blog} key={blog.id} dataIndex={index.toString()} />
        ))}
        {featuredPage && <BlogCardFeature blog={featuredPage} />}
        {currentBlogs.slice(3).map((blog, index) => (
          <BlogCard blog={blog} key={blog.id} dataIndex={(index + 3).toString()} />
        ))}
      </div>
      {currentBlogs.length > 0 && totalPages > 1 && (
        <div className="flex justify-center px-margin">
          <BlogPagination activePage={clampedPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      )}
    </div>
  )
}

function RenderContent() {
  scrollHistoryManager.updateHistoryState()
  return <BlogGrid />
}

function initBlogGrid() {
  const container = document.getElementById('list-blogs')
  const hasBlog = window.globals?.currentBlog
  const hasArticles = (hasBlog?.articles?.length ?? 0) > 0
  const hasFeatured = !!hasBlog?.featuredPage
  if (container && (hasArticles || hasFeatured)) {
    render(<RenderContent />, container)
  } else if (container && !hasBlog) {
    setTimeout(initBlogGrid, 100)
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBlogGrid)
} else {
  initBlogGrid()
}
